import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatDate, formatApiError } from "../lib/api";
import { fmtMoney, fmtPct, fmtNumber, StatusPill, AgingChip, ALL_STATUSES, AGING_STYLES } from "../components/ops/opsUtils";
import { Search, Filter, Download, Plus, ChevronUp, ChevronDown, Archive, Trash2, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { v: "-funding_date", l: "Newest funded" },
  { v: "funding_date", l: "Oldest funded" },
  { v: "-principal", l: "Largest principal" },
  { v: "principal", l: "Smallest principal" },
  { v: "client_name", l: "Client A → Z" },
];

export default function LoansIndex() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loans, setLoans] = useState([]);
  const [allLoansCount, setAllLoansCount] = useState(null);
  const [loading, setLoading] = useState(true);

  const search = params.get("search") || "";
  // Default to ACTIVE-only view so settled loans don't inflate the totals.
  // Pass ?status=all to see everything. `archived` is a special view that
  // shows only soft-deleted records.
  const status = params.get("status") || "active";
  const aging = params.get("aging") || "all";
  const sort = params.get("sort") || "-funding_date";
  const archived = status === "archived";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, status, aging, sort]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ sort, limit: "5000" });
      if (search) qs.set("search", search);
      if (archived) {
        qs.set("deleted_only", "true");
      } else if (status === "active") {
        // "active" is a UI bucket — translate to the canonical active statuses
        qs.set("status", "Funded,Active,PendingSettlement,PayoffRequested");
      } else if (status !== "all") {
        qs.set("status", status);
      }
      if (aging !== "all") qs.set("aging", aging);
      const { data } = await api.get(`/operations/loans?${qs.toString()}`);
      setLoans(data);
      // Also fetch the all-time count once so we can show "X of Y" in the header
      if (allLoansCount === null) {
        api.get(`/operations/loans?limit=1`).then((r) => {
          // The list endpoint returns 1 row when limit=1; we need a real count.
          // Simplest: hit the dashboard endpoint which already aggregates totals.
          api.get(`/operations/dashboard`).then((d) => {
            const k = d.data.kpis || {};
            setAllLoansCount(k.active_loans + k.settled_loans + (k.status_distribution?.pre_funding || 0));
          }).catch(() => {});
        }).catch(() => {});
      }
    } finally { setLoading(false); }
  };

  const softDeleteLoan = async (loan, e) => {
    e.stopPropagation();
    const reason = window.prompt(`Soft-delete loan ${loan.loan_id} for ${loan.client_name}?\n\nIt will be hidden everywhere but recoverable from the Archived view. Optionally enter a reason:`);
    if (reason === null) return;
    try {
      await api.delete(`/operations/loans/${loan.loan_id}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`);
      toast.success(`Loan archived — visible in Archived view.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const restoreLoan = async (loan, e) => {
    e.stopPropagation();
    try {
      await api.post(`/operations/loans/${loan.loan_id}/restore`);
      toast.success(`Loan restored.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const totals = useMemo(() => {
    // For ACTIVE-only views, exclude settled loans from current_payoff math.
    // (When viewing "all", a settled loan's current_payoff is its settled_amount
    // which is meaningful as "what we collected" but NOT "outstanding payoff" —
    // so we explicitly zero it out for settled rows here too.)
    const visible = loans;
    const principal = visible.reduce((s, l) => s + (l.principal || 0), 0);
    const payoff = visible.reduce((s, l) => {
      const closed = l.current_status === "Settled" || l.current_status === "Closed";
      return s + (closed ? 0 : (l.current_payoff || 0));
    }, 0);
    return { principal, payoff, count: visible.length };
  }, [loans]);

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="loans-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Operations</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Loans</h1>
          <div className="text-sm text-[#6B7280] mt-1.5 flex flex-wrap items-baseline gap-x-2">
            <span>
              <span className="font-medium text-[#0A2540] tabular-nums">{fmtNumber(totals.count)}</span> {status === "active" ? "active" : status === "all" ? "loans" : status}
            </span>
            <span>·</span>
            <span><span className="font-medium text-[#0A2540] tabular-nums">{fmtMoney(totals.principal)}</span> principal</span>
            <span>·</span>
            <span><span className="font-medium text-[#0066FF] tabular-nums">{fmtMoney(totals.payoff)}</span> outstanding payoff</span>
            {allLoansCount && status !== "all" && (
              <button
                onClick={() => setParam("status", "all")}
                className="ml-2 text-[11px] text-[#0066FF] hover:underline"
                data-testid="loans-view-all-link"
              >
                View all {allLoansCount}
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/loans/new">
            <Button className="bg-[#0066FF] hover:bg-[#0052CC] h-9" data-testid="loans-add-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add funding</Button>
          </Link>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input
            placeholder="Search by client, attorney email, loan ID…"
            defaultValue={search}
            onKeyDown={(e) => e.key === "Enter" && setParam("search", e.currentTarget.value.trim())}
            onBlur={(e) => setParam("search", e.currentTarget.value.trim())}
            className="pl-9 h-9"
            data-testid="loans-search"
          />
        </div>
        <Select value={status} onValueChange={(v) => setParam("status", v)}>
          <SelectTrigger className="h-9 w-[180px]" data-testid="loans-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active (default)</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
            {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            <SelectItem value="archived">Archived (soft-deleted)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={aging} onValueChange={(v) => setParam("aging", v)}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="loans-aging-filter"><SelectValue placeholder="Aging" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ages</SelectItem>
            {Object.keys(AGING_STYLES).filter((k) => k !== "settled").map((k) => <SelectItem key={k} value={k}>{AGING_STYLES[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="loans-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{SORT_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
        {(search || status !== "all" || aging !== "all") && (
          <Button variant="ghost" className="h-9" onClick={() => setParams({})} data-testid="loans-clear-filters">Clear</Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-2.5 font-medium">Client</th>
                <th className="px-4 py-2.5 font-medium">Attorney</th>
                <th className="px-4 py-2.5 font-medium">Funded</th>
                <th className="px-4 py-2.5 font-medium text-right">Principal</th>
                <th className="px-4 py-2.5 font-medium text-right">Current payoff</th>
                <th className="px-4 py-2.5 font-medium text-right">GP</th>
                <th className="px-4 py-2.5 font-medium">Rate</th>
                <th className="px-4 py-2.5 font-medium">Aging</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="p-10 text-center text-[#6B7280]" data-testid="loans-loading">Loading loans…</td></tr>
              )}
              {!loading && loans.length === 0 && (
                <tr><td colSpan={10} className="p-10 text-center text-[#6B7280]" data-testid="loans-empty">
                  {archived ? "Nothing in the archive." : <>No loans match these filters. <Link to="/admin/loans/new" className="text-[#0066FF] hover:underline">Add the first one →</Link></>}
                </td></tr>
              )}
              {!loading && loans.map((l, i) => (
                <tr key={l.loan_id} data-testid={`loans-row-${i}`}
                    onClick={() => navigate(`/admin/loans/${l.loan_id}`)}
                    className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors cursor-pointer ${archived ? "opacity-70" : ""}`}>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/admin/loans/${l.loan_id}`} className="text-[#0A2540] font-medium hover:text-[#0066FF]">{l.client_name}</Link>
                    <div className="text-[11px] text-[#9CA3AF] tabular-nums mt-0.5">{l.loan_id}{l.is_deleted ? <span className="ml-2 text-[#B91C1C]">· archived</span> : null}</div>
                  </td>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/admin/attorneys/${encodeURIComponent(l.attorney_email)}`} className="text-[#374151] hover:text-[#0066FF]">{l.attorney_name || l.attorney_email}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(l.funding_date)}</td>
                  <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums font-medium">{fmtMoney(l.principal)}</td>
                  <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums">{fmtMoney(l.current_payoff)}</td>
                  <td className="px-4 py-2.5 text-right text-[#047857] tabular-nums">{fmtMoney(l.accrued_interest)}</td>
                  <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{(l.monthly_rate * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2.5"><AgingChip bucket={l.aging_bucket} /></td>
                  <td className="px-4 py-2.5"><StatusPill status={l.current_status} /></td>
                  <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    {archived || l.is_deleted ? (
                      <button onClick={(e) => restoreLoan(l, e)} title="Restore" data-testid={`loan-restore-${l.loan_id}`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded text-[#047857] hover:bg-[#ECFDF5]">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button onClick={(e) => softDeleteLoan(l, e)} title="Archive (soft-delete)" data-testid={`loan-delete-${l.loan_id}`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-[#FEF2F2]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
